import { useDocChatCapabilities } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatCapabilities'
import { useDocChatModelSettings } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatModelSettings'
import {
  type DocChatModelOptions,
  reconcileDocChatModelSettings,
  resolveDocChatModelOptions,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatModels'
import { useEffect, useMemo } from 'react'

export type ResolvedDocChatModelSettings = {
  options: DocChatModelOptions
  /** The stored model reconciled against the live backend options. */
  model: string
  /** The stored thinking level reconciled against the resolved model. */
  thinkingLevel: string
  /** True once the backend capabilities have loaded. */
  loaded: boolean
  isInternal: boolean
  permissionsReady: boolean
  setModel: (model: string) => void
  setThinkingLevel: (thinkingLevel: string) => void
}

/**
 * The single source of the model/thinking-level the UI shows and chat requests
 * send. The persisted choice is reconciled against the backend capabilities so
 * a model from another environment (or a retired one) is never sent; callers
 * must gate on `loaded` before sending, so a request that starts before
 * capabilities arrive omits the fields and lets the backend pick its default.
 */
export function useResolvedDocChatModelSettings(
  siteId: string
): ResolvedDocChatModelSettings {
  const { data: capabilities } = useDocChatCapabilities(siteId)
  const {
    isInternal,
    permissionsReady,
    model,
    thinkingLevel,
    setModel,
    setThinkingLevel,
  } = useDocChatModelSettings()

  const loaded = capabilities !== undefined
  const options = useMemo(
    () => resolveDocChatModelOptions(capabilities),
    [capabilities]
  )
  const resolved = useMemo(
    () => reconcileDocChatModelSettings(options, { model, thinkingLevel }),
    [options, model, thinkingLevel]
  )

  // Persist the reconciled choice once the real options are known, so the store,
  // the menu, and outgoing requests agree. Reconciling against the built-in
  // fallback would clobber a persisted choice before capabilities load.
  useEffect(() => {
    if (!loaded || !isInternal) return
    if (resolved.model !== model) setModel(resolved.model)
    if (resolved.thinkingLevel !== thinkingLevel)
      setThinkingLevel(resolved.thinkingLevel)
  }, [
    loaded,
    isInternal,
    resolved.model,
    resolved.thinkingLevel,
    model,
    thinkingLevel,
    setModel,
    setThinkingLevel,
  ])

  return {
    options,
    model: resolved.model,
    thinkingLevel: resolved.thinkingLevel,
    loaded,
    isInternal,
    permissionsReady,
    setModel,
    setThinkingLevel,
  }
}
